import axios from "axios";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { PaginationMeta } from "@ui";

export interface Document {
  id: number;
  filename: string;
  content_type: string;
  byte_size: number;
  ocr_status: "pending" | "processing" | "completed" | "failed" | "skipped";
  thumbnail_url: string | null;
  file_url: string | null;
  ocr_content?: string | null;
  created_at: string;
  updated_at: string;
}

export interface DocumentsResponse {
  documents: Document[];
  meta: PaginationMeta;
}

const BASE_URL = "/system/agentic/documents";

// API functions
export const documentApi = {
  list: async (page = 1): Promise<DocumentsResponse> => {
    const { data } = await axios.get(`${BASE_URL}.json`, { params: { page } });
    return data;
  },

  get: async (id: number): Promise<Document> => {
    const { data } = await axios.get(`${BASE_URL}/${id}.json`);
    return data;
  },

  upload: async (
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<Document> => {
    const formData = new FormData();
    formData.append("file", file);

    const { data } = await axios.post(`${BASE_URL}.json`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          onProgress(progress);
        }
      },
    });
    return data;
  },

  delete: async (id: number): Promise<void> => {
    await axios.delete(`${BASE_URL}/${id}.json`);
  },

  search: async (query: string, limit = 5): Promise<{ documents: Document[] }> => {
    const { data } = await axios.get(`${BASE_URL}/search.json`, {
      params: { q: query, limit },
    });
    return data;
  },
};

// Query keys
export const documentKeys = {
  all: ["documents"] as const,
  list: (page: number) => ["documents", "list", page] as const,
  detail: (id: number) => ["documents", id] as const,
};

// Hooks
export function useDocuments(page = 1) {
  return useQuery({
    queryKey: documentKeys.list(page),
    queryFn: () => documentApi.list(page),
  });
}

export function useDocument(id: number) {
  return useQuery({
    queryKey: documentKeys.detail(id),
    queryFn: () => documentApi.get(id),
    enabled: !!id,
  });
}

export function useSearchDocuments(query: string, limit = 5) {
  return useQuery({
    queryKey: ["documents", "search", query, limit] as const,
    queryFn: () => documentApi.search(query, limit),
    enabled: query.length >= 2,
  });
}

export function useUploadDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      onProgress,
    }: {
      file: File;
      onProgress?: (progress: number) => void;
    }) => documentApi.upload(file, onProgress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: documentApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: documentKeys.all });
    },
  });
}

// Utility functions
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

export function getFileTypeIcon(contentType: string): string {
  if (contentType.startsWith("image/")) return "image";
  if (contentType === "application/pdf") return "file-text";
  if (contentType.includes("wordprocessingml")) return "file-text";
  if (contentType.includes("spreadsheetml")) return "table";
  return "file";
}

export const ALLOWED_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];

export const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30MB
